import React, { useEffect, useState } from 'react';
import { fetchWithAuth } from '../services/api';

export default function UsersAccess() {
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [message, setMessage] = useState('');
  const [pending, setPending] = useState(false);
  const [invite, setInvite] = useState({ name: '', email: '' });

  async function load() {
    try {
      const response = await fetchWithAuth('/api/auth/users');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setUsers(data.users); setCatalog(data.catalog);
    } catch (e) { setMessage(e.message); }
  }
  useEffect(() => { load(); }, []);
  const change = (id, field, value) => setUsers(items => items.map(u => u.id === id ? { ...u, [field]: value } : u));
  async function save(user) {
    setPending(true); setMessage('');
    try {
      const response = await fetchWithAuth('/api/auth/users/' + user.id, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(user),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setUsers(items => items.map(u => u.id === user.id ? data : u));
      setMessage('Access saved for ' + user.name + '.');
    } catch (e) { setMessage(e.message); }
    finally { setPending(false); }
  }
  async function revoke(user) {
    setPending(true);
    try {
      const response = await fetchWithAuth('/api/auth/users/' + user.id + '/revoke-sessions', { method: 'POST' });
      if (!response.ok) throw new Error((await response.json()).error);
      setMessage('Signed out all sessions for ' + user.name + '.');
    } catch (e) { setMessage(e.message); }
    finally { setPending(false); }
  }
  async function add(e) {
    e.preventDefault(); setPending(true);
    try {
      const response = await fetchWithAuth('/api/auth/users', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...invite, tabs: {}, overrides: {} }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setUsers(items => [...items, data]); setInvite({ name: '', email: '' });
      setMessage('User approved. Select tabs and save to grant access.');
    } catch (err) { setMessage(err.message); }
    finally { setPending(false); }
  }
  return <section className="space-y-5">
    <h1 className="text-2xl font-semibold">Admin</h1>
    <p className="text-gray-600">Choose website tabs for each person. New subtabs inherit their main tab. Changes take effect when saved.</p>
    {message && <p role="status" className="p-3 bg-blue-50 rounded">{message}</p>}
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead><tr className="bg-gray-100">
          <th className="p-3 text-left">User</th><th>Active</th>
          {catalog.map(tab => <th key={tab.id}>{tab.label}</th>)}
          <th>Manage users</th><th>Actions</th>
        </tr></thead>
        <tbody>{users.map(user => <React.Fragment key={user.id}>
          <tr className="border-b">
            <td className="p-3"><strong>{user.name}</strong><div className="text-gray-500">{user.email}</div></td>
            <td className="text-center"><input aria-label={user.name + ' active'} type="checkbox" checked={user.active}
              onChange={e => change(user.id, 'active', e.target.checked)} /></td>
            {catalog.map(tab => <td key={tab.id} className="p-3 text-center">
              <input aria-label={user.name + ' ' + tab.label} type="checkbox" checked={Boolean(user.tabs[tab.id])}
                onChange={e => change(user.id, 'tabs', { ...user.tabs, [tab.id]: e.target.checked })} />
              <button className="block text-xs text-blue-700 mx-auto mt-1" onClick={() => setExpanded({ ...expanded, [user.id + tab.id]: !expanded[user.id + tab.id] })}>Subtabs</button>
            </td>)}
            <td className="text-center"><input aria-label={user.name + ' manage users'} type="checkbox" checked={user.manage_users}
              onChange={e => change(user.id, 'manage_users', e.target.checked)} /></td>
            <td className="p-3 space-x-2 whitespace-nowrap">
              <button disabled={pending} onClick={() => save(user)} className="bg-blue-600 text-white rounded px-3 py-2">Save</button>
              <button disabled={pending} onClick={() => revoke(user)} className="underline">Sign out sessions</button>
            </td>
          </tr>
          {catalog.filter(tab => expanded[user.id + tab.id]).map(tab => <tr key={tab.id} className="bg-gray-50">
            <td colSpan={catalog.length + 4} className="p-4">
              <div className="font-semibold mb-2">{user.name} / {tab.label}</div>
              <div className="flex gap-5 flex-wrap">{tab.children.map(child => {
                const inherit = !(child.id in user.overrides);
                return <label key={child.id} className="flex items-center gap-2">
                  <input type="checkbox" disabled={!user.tabs[tab.id]}
                    checked={Boolean(user.tabs[tab.id] && user.overrides[child.id] !== false)}
                    onChange={e => {
                      const overrides = { ...user.overrides };
                      if (e.target.checked) delete overrides[child.id]; else overrides[child.id] = false;
                      change(user.id, 'overrides', overrides);
                    }} />
                  {child.label}<span className="text-xs text-gray-500">{inherit ? '(inherits)' : '(excluded)'}</span>
                </label>;
              })}</div>
              <button className="mt-3 text-blue-700 underline" onClick={() => {
                const overrides = { ...user.overrides };
                tab.children.forEach(child => delete overrides[child.id]);
                change(user.id, 'overrides', overrides);
              }}>Reset subtabs to parent</button>
            </td>
          </tr>)}
        </React.Fragment>)}</tbody>
      </table>
    </div>
    <form onSubmit={add} className="flex flex-wrap gap-3 pt-4">
      <input required placeholder="Name" aria-label="New user name" className="border rounded px-3 py-2" value={invite.name}
        onChange={e => setInvite({ ...invite, name: e.target.value })} />
      <input required type="email" placeholder="Primary work Google email" aria-label="New user email" className="border rounded px-3 py-2 min-w-72" value={invite.email}
        onChange={e => setInvite({ ...invite, email: e.target.value })} />
      <button disabled={pending} className="border rounded px-4 py-2">Add user</button>
    </form>
  </section>;
}
